import { Check, Copy, Minus, Plus } from "lucide-react";
import { useState } from "react";

import { MAX_BATCH_ITEMS } from "../../lib/batch";
import { DestinationCsvImport } from "./DestinationCsvImport";
import { clampDelegateQuantity } from "./delegate-distribution";

import "./delegate-distribution.css";

interface DelegateDistributionProps {
  delegateId: string;
  quantity: number;
  recipients: string[];
  defaultRecipient?: string;
  onDelegateId: (value: string) => void;
  onQuantity: (value: number) => void;
  onRecipient: (index: number, value: string) => void;
  onCopyFirst: () => void;
  importedRecipientCount?: number;
  onImportRecipients: (addresses: string[]) => void;
  onClearImportedRecipients: () => void;
}

export function DelegateDistribution({
  delegateId,
  quantity,
  recipients,
  defaultRecipient,
  onDelegateId,
  onQuantity,
  onRecipient,
  onCopyFirst,
  importedRecipientCount,
  onImportRecipients,
  onClearImportedRecipients,
}: DelegateDistributionProps) {
  const [copied, setCopied] = useState(false);
  const firstRecipient = recipients[0]?.trim() ?? "";
  const setQuantity = (value: number) => onQuantity(clampDelegateQuantity(value));
  const copyFirst = () => {
    onCopyFirst();
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1_600);
  };

  return <div className="delegate-builder">
    <div className="delegate-builder-heading">
      <div>
        <span>DELEGATE SOURCE</span>
        <strong>ONE SOURCE · {quantity} INSCRIPTION{quantity === 1 ? "" : "S"}</strong>
      </div>
      <div className="delegate-quantity" aria-label="Delegate quantity">
        <button type="button" aria-label="Decrease quantity" disabled={quantity <= 1} onClick={() => setQuantity(quantity - 1)}><Minus /></button>
        <label><span>QUANTITY</span><input type="number" min={1} max={MAX_BATCH_ITEMS} value={quantity} onChange={(event) => setQuantity(Number(event.target.value))} /></label>
        <button type="button" aria-label="Increase quantity" disabled={quantity >= MAX_BATCH_ITEMS} onClick={() => setQuantity(quantity + 1)}><Plus /></button>
      </div>
    </div>
    <label className="field delegate-source-field"><span>INSCRIPTION ID</span><input value={delegateId} onChange={(event) => onDelegateId(event.target.value)} placeholder={`${"0".repeat(64)}i0`} /></label>
    <div className="delegate-destinations">
      <header>
        <div><span>DESTINATIONS</span><small>LEAVE BLANK TO USE THE CONNECTED ORDINALS WALLET</small></div>
        <button className={copied ? "delegate-copy-all copied" : "delegate-copy-all"} type="button" disabled={!firstRecipient || quantity < 2} onClick={copyFirst}>
          {copied ? <Check /> : <Copy />} {copied ? "APPLIED" : "COPY FIRST TO ALL"}
        </button>
      </header>
      <div className="delegate-destination-list">
        {recipients.map((recipient, index) => <label className="delegate-destination-row" key={index}>
          <span>{String(index + 1).padStart(2, "0")}</span>
          <input
            aria-label={`Destination for delegate ${index + 1}`}
            value={recipient}
            onChange={(event) => onRecipient(index, event.target.value)}
            placeholder={defaultRecipient || "CONNECTED ORDINALS WALLET"}
            autoComplete="off"
            spellCheck={false}
          />
        </label>)}
      </div>
      <DestinationCsvImport
        itemCount={quantity}
        mappingKey={`delegates:${quantity}`}
        appliedCount={importedRecipientCount}
        onApply={onImportRecipients}
        onClear={onClearImportedRecipients}
      />
    </div>
  </div>;
}
